import { useState, useCallback } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { RefreshCw, TrendingUp, Users, DollarSign, Video, AlertTriangle, ArrowUpRight } from "lucide-react";

// ── Theme constants ────────────────────────────────────────────────────────────
const CARD = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 24 } as const;
const MUTED = "#94a3b8";
const INDIGO = "#6366f1";
const VIOLET = "#8b5cf6";
const AMBER = "#f59e0b";
const SLATE = "#64748b";
const GREEN = "#22c55e";
const RED = "#ef4444";

const TIER_COLOR: Record<string, string> = {
  free: SLATE,
  starter: INDIGO,
  professional: VIOLET,
  enterprise: AMBER,
};

// ── Skeleton helper ────────────────────────────────────────────────────────────
function Skeleton({ w = "100%", h = 24, r = 6 }: { w?: string | number; h?: number; r?: number }) {
  return (
    <div
      style={{
        width: w, height: h, borderRadius: r,
        background: "linear-gradient(90deg,rgba(255,255,255,0.05) 25%,rgba(255,255,255,0.10) 50%,rgba(255,255,255,0.05) 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
      }}
    />
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({
  label, value, icon: Icon, accent = INDIGO, sub,
}: {
  label: string; value: string | number; icon: any; accent?: string; sub?: string;
}) {
  return (
    <div style={CARD}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: MUTED, fontWeight: 500 }}>{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${accent}22`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon style={{ width: 16, height: 16, color: accent }} />
        </div>
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: "#fff", lineHeight: 1, marginBottom: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: MUTED }}>{sub}</div>}
    </div>
  );
}

// ── Custom tooltip ─────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#1e2235", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, padding: "8px 12px" }}>
      <p style={{ color: MUTED, fontSize: 12, marginBottom: 4 }}>{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color ?? "#fff", fontSize: 13, fontWeight: 600 }}>{p.value} signups</p>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MotherAnalytics() {
  const [refetchKey, setRefetchKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["/api/mother/analytics", refetchKey],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/mother/analytics");
      if (!res.ok) throw new Error("Failed to fetch analytics");
      const json = await res.json();
      setLastUpdated(new Date());
      return json;
    },
    retry: false,
  });

  const handleRefresh = useCallback(() => {
    setRefetchKey(k => k + 1);
    refetch();
  }, [refetch]);

  if (isError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 12 }}>
        <AlertTriangle style={{ width: 32, height: 32, color: RED }} />
        <p style={{ color: MUTED, fontSize: 14 }}>Failed to load analytics. Check server logs.</p>
        <button onClick={handleRefresh} style={{ background: INDIGO, color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontSize: 13 }}>
          Retry
        </button>
      </div>
    );
  }

  const d = data as any;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }} data-testid="analytics-panel">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#fff", margin: 0 }}>Analytics</h1>
          <p style={{ fontSize: 13, color: MUTED, margin: "4px 0 0" }}>
            Platform-wide metrics and growth
            {lastUpdated && (
              <> &mdash; updated {lastUpdated.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</>
            )}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.25)",
            color: "#c7d2fe", borderRadius: 8, padding: "8px 14px", fontSize: 13,
            cursor: isLoading ? "wait" : "pointer", transition: "all 0.15s",
          }}
          data-testid="button-refresh-analytics"
        >
          <RefreshCw style={{ width: 14, height: 14, animation: isLoading ? "spin 1s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* ── Row 1: Top stat cards ────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 16 }}>
        {isLoading ? Array.from({ length: 6 }).map((_, i) => (
          <div key={i} style={CARD}><Skeleton h={80} /></div>
        )) : (
          <>
            <StatCard label="Total Workspaces" value={d.totalWorkspaces} icon={Users} accent={INDIGO} sub="Active, non-blocked" data-testid="stat-total-workspaces" />
            <StatCard label="Active This Month" value={d.activeWorkspaces} icon={TrendingUp} accent={GREEN} sub="Sent assessment in 30 days" />
            <StatCard label="MRR (NZD)" value={`$${(d.stripe?.mrrNzd ?? 0).toLocaleString()}`} icon={DollarSign} accent={AMBER} sub="Active subscriptions" />
            <StatCard label="Candidates Assessed" value={(d.totalCandidatesAllTime ?? 0).toLocaleString()} icon={Video} accent={VIOLET} sub="All time" />
            <StatCard label="Flagged Accounts" value={d.flaggedAccounts ?? 0} icon={AlertTriangle} accent={AMBER} sub="Pending review" />
            <StatCard label="Conversion Rate" value={d.conversionRate ?? "0.0%"} icon={ArrowUpRight} accent={GREEN} sub="Free → Paid" />
            <StatCard label="Activation Rate" value={d.activationRate ?? "0.0%"} icon={TrendingUp} accent={INDIGO} sub="Sent at least 1 assessment" />
          </>
        )}
      </div>

      {/* ── Row 2: Tier breakdown ────────────────────────────────────────────── */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>Tier Breakdown</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
          {["free", "starter", "professional", "enterprise"].map(tier => (
            <div key={tier} style={{ ...CARD, padding: 16, borderLeft: `3px solid ${TIER_COLOR[tier]}` }}>
              {isLoading ? <Skeleton h={48} /> : (
                <>
                  <div style={{ fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
                    {d.tierBreakdown?.[tier] ?? 0}
                  </div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 6, textTransform: "capitalize" }}>{tier}</div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Row 3: Daily signups line chart ─────────────────────────────────── */}
      <div style={CARD}>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: "#fff", margin: "0 0 16px" }}>Daily Signups — Last 30 Days</h2>
        {isLoading ? <Skeleton h={200} /> : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={d.signupsLast30Days ?? []} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                tick={{ fill: MUTED, fontSize: 11 }}
                tickFormatter={v => v.slice(5)}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tick={{ fill: MUTED, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone" dataKey="signups" stroke={INDIGO} strokeWidth={2}
                dot={{ fill: INDIGO, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: INDIGO }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Row 4: Weekly bar + Stripe health ───────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Weekly bar chart */}
        <div style={CARD}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "#fff", margin: "0 0 16px" }}>Weekly Signups — Last 12 Weeks</h2>
          {isLoading ? <Skeleton h={200} /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.signupsLast12Weeks ?? []} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: MUTED, fontSize: 11 }}
                  tickFormatter={v => v.slice(5)}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis tick={{ fill: MUTED, fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="signups" fill={VIOLET} radius={[3, 3, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Stripe health panel */}
        <div style={CARD}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "#fff", margin: "0 0 16px" }}>Stripe Subscription Health</h2>
          {isLoading ? <Skeleton h={200} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
              {[
                { label: "Active", value: d.stripe?.activeSubscriptions ?? 0, color: GREEN, dot: GREEN },
                { label: "Past Due", value: d.stripe?.pastDueSubscriptions ?? 0, color: AMBER, dot: AMBER },
                { label: "Cancelled", value: d.stripe?.cancelledSubscriptions ?? 0, color: RED, dot: RED },
              ].map(({ label, value, color, dot }) => (
                <div key={label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: dot, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 14, color: "#e2e8f0" }}>{label}</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color }} data-testid={`stripe-${label.toLowerCase().replace(" ", "-")}`}>{value}</span>
                </div>
              ))}
              <div style={{ marginTop: 8, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, color: MUTED }}>Monthly Recurring Revenue</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: AMBER }} data-testid="stripe-mrr">${(d.stripe?.mrrNzd ?? 0).toLocaleString()} NZD</span>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ── Row 5: Candidates ────────────────────────────────────────────────── */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>Candidates Assessed</h2>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={CARD}>
            {isLoading ? <Skeleton h={56} /> : (
              <>
                <div style={{ fontSize: 36, fontWeight: 700, color: "#fff", lineHeight: 1 }} data-testid="stat-candidates-alltime">
                  {(d.totalCandidatesAllTime ?? 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 13, color: MUTED, marginTop: 8 }}>All time</div>
              </>
            )}
          </div>
          <div style={CARD}>
            {isLoading ? <Skeleton h={56} /> : (
              <>
                <div style={{ fontSize: 36, fontWeight: 700, color: "#fff", lineHeight: 1 }} data-testid="stat-candidates-30d">
                  {(d.totalCandidatesLast30Days ?? 0).toLocaleString()}
                </div>
                <div style={{ fontSize: 13, color: MUTED, marginTop: 8 }}>Last 30 days</div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 6: Activation by Tier ─────────────────────────────────────────── */}
      <div>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>Activation by Tier</h2>
        <div style={CARD}>
          {isLoading ? <Skeleton h={120} /> : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ color: MUTED, textAlign: "left" }}>
                  <th style={{ padding: "0 0 10px", fontWeight: 500 }}>Tier</th>
                  <th style={{ padding: "0 0 10px", fontWeight: 500 }}>Total Workspaces</th>
                  <th style={{ padding: "0 0 10px", fontWeight: 500 }}>Activated</th>
                  <th style={{ padding: "0 0 10px", fontWeight: 500 }}>Rate</th>
                  <th style={{ padding: "0 0 10px", fontWeight: 500, width: 160 }}></th>
                </tr>
              </thead>
              <tbody>
                {(d.activationByTier ?? []).map((row: any) => {
                  const pct = parseFloat(row.rate) || 0;
                  const color = TIER_COLOR[row.tier] ?? SLATE;
                  return (
                    <tr key={row.tier} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <td style={{ padding: "10px 0", color: "#fff", textTransform: "capitalize", fontWeight: 600 }}>{row.tier}</td>
                      <td style={{ padding: "10px 0", color: MUTED }}>{row.total}</td>
                      <td style={{ padding: "10px 0", color: "#fff" }}>{row.activated}</td>
                      <td style={{ padding: "10px 0", color, fontWeight: 600 }}>{row.rate}</td>
                      <td style={{ padding: "10px 0" }}>
                        <div style={{ height: 6, borderRadius: 3, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 3, transition: "width 0.4s" }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!d.activationByTier || d.activationByTier.length === 0) && (
                  <tr><td colSpan={5} style={{ padding: "16px 0", color: MUTED, textAlign: "center" }}>No data yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}
